/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/case.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Accepts pairs of conditions and values. The function returns the value that
 * belongs to the first condition that evaluates to `true`. Both the conditions
 * and the returned values can be any expression, including column references.
 *
 * If the number of arguments is odd, the last argument is the default value which
 * is returned when no condition matches. If the number of arguments is even, and
 * no condition matches, the function returns `null`.
 *
 * @example
 * FROM employees
 * | EVAL type = CASE(
 *     languages <= 1, "monolingual",
 *     languages <= 2, "bilingual",
 *      "polyglot")
 * | KEEP emp_no, languages, type
 *
 * @example
 * FROM sample_data
 * | EVAL successful = CASE(
 *     STARTS_WITH(message, "Connected to"), 1,
 *     message == "Connection error", 0
 *   )
 * | STATS success_rate = AVG(successful)
 *
 * @example
 * FROM sample_data
 * | EVAL error = CASE(message LIKE "*error*", 1, 0)
 * | EVAL hour = DATE_TRUNC(1 hour, @timestamp)
 * | STATS error_rate = AVG(error) by hour
 * | SORT hour
 *
 * @example
 * FROM sample_data
 * | EVAL error_message = CASE(message LIKE "*error*", message, null)
 * | STATS distinct_error_messages = COUNT_DISTINCT(error_message)
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'case',
  signatures: [
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'aggregate_metric_double',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'aggregate_metric_double',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'aggregate_metric_double',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'aggregate_metric_double',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'aggregate_metric_double',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'boolean',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'cartesian_point',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'cartesian_point',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'cartesian_shape',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'cartesian_shape',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'date',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'date',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'date',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'date_nanos',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'date_range',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'date_range',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'date_range',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'date_range',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'date_range',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'dense_vector',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'dense_vector',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'dense_vector',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'dense_vector',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'dense_vector',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'double',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'double',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'double',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'double_range',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'double_range',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'double_range',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'double_range',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'double_range',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'exponential_histogram',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'exponential_histogram',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'exponential_histogram',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'exponential_histogram',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'exponential_histogram',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'flattened',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'flattened',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geo_point',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'geo_point',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geo_shape',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'geo_shape',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geohash',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'geohash',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geohex',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'geohex',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geotile',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'geotile',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'histogram',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'histogram',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'histogram',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'histogram',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'histogram',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'integer',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'ip',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'ip',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'text',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'long',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'long',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'long',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'tdigest',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'tdigest',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'tdigest',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'tdigest',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'tdigest',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'text',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'text',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'text',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'text',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'unsigned_long',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'version',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'version',
    },
    {
      params: [
        {
          name: 'condition',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'trueValue',
          type: 'version',
          optional: false,
        },
        {
          name: 'elseValue',
          type: 'version',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'version',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
